import { useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
  BackLink,
  DataTable,
  EmptyState,
  ErrorNotice,
  LoadingState,
  PageHeading,
  Pagination,
  Panel,
  SearchInput,
  SegmentedControl,
  StatTile,
  TrendChart,
  type DataTableSort,
  type TrendChartVariant,
} from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import { useTableSort } from '@/lib/use-table-sort';
import { useUrlSearch } from '@/lib/use-list-query-state';
import {
  CHART_VARIANTS,
  INTERVAL_LABELS,
  LIVE_REFRESH_MS,
  RANGES,
  periodLabel,
  rangeWindow,
  type RangeKey,
} from '@/lib/report-window';
import {
  PAGE_TRAFFIC,
  TRAFFIC_OPTIONS,
  readPageTypes,
  templateLabel,
  trafficSearch,
  useTrafficParam,
  type PageTraffic,
  type PageTypeRow,
} from '@/lib/page-types';
import { listPages, PAGE_SORT, PAGE_SORT_KEYS } from '@/lib/pages';
import { eventsToMarkers, listAnalyticsEvents } from '@/lib/analytics-events';
import { InfoTip, METRIC_INFO, METRIC_LABEL } from '@/components/metric-info';
import { PercentChange, PositionChange, firstSeenLabel, lastSeenLabel, placesSuffix, positionHint, positionLabel } from '@/components/page-trend';
import { useIntervalChoice } from '@/lib/use-interval-choice';
import { NoPaidDataState, PAID_COVERAGE, PaidCoverageNote, paidCoverage } from '@/components/paid-traffic';

const DETAIL_RANGE_KEYS: readonly RangeKey[] = ['7d', '30d', '90d', '12m'];
const PAGE_SIZE = 25;
const DEFAULT_SORT: DataTableSort = { key: PAGE_SORT.sessions, direction: 'desc' };

/** What the chart can show; each stat tile picks its own. */
const CHART_METRICS = {
  sessions: { label: 'Sessions', unit: 'sessions' },
  impressions: { label: 'Impressions', unit: 'impressions' },
  position: { label: 'Avg position', unit: 'avg position' },
  leads: { label: 'Leads', unit: 'leads' },
  cvr: { label: METRIC_LABEL.conversionRate, unit: 'conversion rate' },
  clicks: { label: 'Clicks', unit: 'clicks' },
} as const;
type ChartMetric = keyof typeof CHART_METRICS;

function cvrLabel(cvr: number | null): string {
  return cvr === null ? '—' : `${(cvr * 100).toFixed(2)}%`;
}
function leadRateLabel(rate: number | null): string {
  return rate === null ? '—' : `${(rate * 100).toFixed(2)}%`;
}
function compactCount(value: number): string {
  return new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(value);
}
function cvrPercent(leads: number, sessions: number): number {
  return sessions > 0 ? Math.round((leads / sessions) * 10000) / 100 : 0;
}

/** One type's per-bucket values for the chosen metric. */
function typeValues(type: PageTypeRow, metric: ChartMetric): number[] {
  switch (metric) {
    case 'sessions':
      return [...type.sessionsSeries];
    case 'leads':
      return [...type.leadsSeries];
    case 'clicks':
      return [...type.clicksSeries];
    case 'impressions':
      return [...type.impressionsSeries];
    case 'position':
      return type.positionSeries.map((position) => (position === null ? 0 : position));
    case 'cvr':
      return type.sessionsSeries.map((sessions, index) => cvrPercent(type.leadsSeries[index] ?? 0, sessions));
    default: {
      const exhaustive: never = metric;
      return exhaustive;
    }
  }
}

/**
 * One page type drilled in (admin-only): its sessions, leads and CVR over time
 * with the global key-event markers, and the list of pages of this type. Click
 * a page to open its full single-page history.
 *
 * @returns The page-type detail screen.
 */
export function PageTypeDetailPage(): React.ReactElement {
  const { type = '' } = useParams<{ type: string }>();
  const [range, setRange] = useState<RangeKey>('30d');
  const [metric, setMetric] = useState<ChartMetric>('sessions');
  const [variant, setVariant] = useState<TrendChartVariant>('bar');
  const [page, setPage] = useState(1);
  const statsWindow = useMemo(() => rangeWindow(range), [range]);
  const [effectiveInterval, setIntervalChoice] = useIntervalChoice(RANGES[range].interval);
  const navigate = useNavigate();
  const { sort, onSort } = useTableSort(PAGE_SORT_KEYS, DEFAULT_SORT);
  const { search, setSearch, debounced } = useUrlSearch();
  const { traffic, setTraffic } = useTrafficParam();
  const paid = traffic === PAGE_TRAFFIC.paid;
  const backTo = `/analytics/page-types${trafficSearch(traffic)}`;
  const openPage = (path: string) => navigate(`/analytics/pages/detail?path=${encodeURIComponent(path)}`);

  const stats = useQuery({
    queryKey: ['page-types', statsWindow.from, statsWindow.to, effectiveInterval, traffic],
    queryFn: ({ signal }) =>
      readPageTypes({ from: statsWindow.from, to: statsWindow.to, interval: effectiveInterval, traffic }, signal),
    refetchInterval: LIVE_REFRESH_MS,
    placeholderData: keepPreviousData,
  });
  const events = useQuery({
    queryKey: ['analytics-events', statsWindow.from, statsWindow.to],
    queryFn: ({ signal }) => listAnalyticsEvents({ from: statsWindow.from, to: statsWindow.to }, signal),
    refetchInterval: LIVE_REFRESH_MS,
    placeholderData: keepPreviousData,
  });
  const pages = useQuery({
    queryKey: ['pages', 'type', type, statsWindow.from, statsWindow.to, page, sort, debounced, traffic],
    queryFn: ({ signal }) =>
      listPages(
        {
          from: statsWindow.from,
          to: statsWindow.to,
          template: type,
          ...(debounced ? { search: debounced } : {}),
          page,
          limit: PAGE_SIZE,
          ...(sort ? { sort: sort.key, dir: sort.direction } : {}),
          traffic,
        },
        signal,
      ),
    refetchInterval: LIVE_REFRESH_MS,
    placeholderData: keepPreviousData,
  });

  if (stats.isPending) return <LoadingState label="Loading page type…" />;
  if (stats.isError) {
    return (
      <section className="animate-portal-rise">
        <BackLink to={backTo}>Page types</BackLink>
        <ErrorNotice title="Page type unavailable" message={errorMessage(stats.error)} />
      </section>
    );
  }

  const row = stats.data.types.find((candidate) => candidate.template === type);
  const markers = eventsToMarkers(events.data ?? []);
  const rangeLabel = RANGES[range].label;
  const coverage = paidCoverage(traffic, stats.data);
  const leadRateInfo = paid ? METRIC_INFO.paidLeadRate : METRIC_INFO.leadRate;

  return (
    <section className="animate-portal-rise">
      <BackLink to={backTo}>Page types</BackLink>
      <div className="mt-2">
        <PageHeading
          eyebrow="PAGE TYPE"
          title={templateLabel(type)}
          subtitle="Sessions, leads and conversion rate for this page type over time, and the pages of this type."
        />
      </div>

      <div className="mb-4 flex min-w-0 flex-wrap gap-3">
        <SegmentedControl<PageTraffic>
          label="Traffic"
          value={traffic}
          onChange={(next) => {
            setTraffic(next);
            setPage(1);
          }}
          options={TRAFFIC_OPTIONS}
        />
        <SegmentedControl<RangeKey>
          label="Range"
          value={range}
          onChange={(next) => {
            setRange(next);
            setPage(1);
          }}
          options={DETAIL_RANGE_KEYS.map((key) => ({ key, label: RANGES[key].label, shortLabel: RANGES[key].shortLabel }))}
        />
        <SegmentedControl
          label="Interval"
          value={effectiveInterval}
          onChange={setIntervalChoice}
          options={(Object.keys(INTERVAL_LABELS) as (keyof typeof INTERVAL_LABELS)[]).map((key) => ({
            key,
            label: INTERVAL_LABELS[key],
          }))}
        />
      </div>

      {coverage === PAID_COVERAGE.partial && stats.data.paidDataFrom !== null ? (
        <PaidCoverageNote from={stats.data.paidDataFrom} />
      ) : null}

      {coverage === PAID_COVERAGE.none ? (
        <NoPaidDataState onShowAll={() => setTraffic(PAGE_TRAFFIC.all)} />
      ) : (
        <>
          {row === undefined ? (
            <Panel>
              <EmptyState
                title="No traffic for this type in the range"
                message="No pages of this type had sessions in the selected range. Try a wider range."
              />
            </Panel>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3 xl:grid-cols-6">
                <StatTile
                  label={paid ? 'Paid sessions' : 'Sessions'}
                  onSelect={() => setMetric('sessions')}
                  selected={metric === 'sessions'}
                  value={row.sessions.toLocaleString()}
                  changePct={row.changePct}
                  hint={`vs ${row.previous.toLocaleString()} the previous ${rangeLabel}`}
                />
                <StatTile
                  label="Impressions"
                  onSelect={() => setMetric('impressions')}
                  selected={metric === 'impressions'}
                  labelAccessory={<InfoTip label="impressions" text={METRIC_INFO.impressions} />}
                  value={compactCount(row.impressions)}
                  changePct={row.impressionsChangePct}
                  hint={`vs ${compactCount(row.previousImpressions)} the previous ${rangeLabel}`}
                />
                <StatTile
                  label="Avg position"
                  onSelect={() => setMetric('position')}
                  selected={metric === 'position'}
                  labelAccessory={<InfoTip label="average position" text={METRIC_INFO.position} />}
                  value={positionLabel(row.avgPosition)}
                  changePct={row.positionChange}
                  changeSuffix={placesSuffix(row.positionChange)}
                  downIsGood
                  hint={positionHint(row.avgPosition, row.previousAvgPosition)}
                />
                <StatTile
                  label={paid ? 'Paid leads' : 'Leads'}
                  onSelect={() => setMetric('leads')}
                  selected={metric === 'leads'}
                  labelAccessory={<InfoTip label="leads" text={METRIC_INFO.leads} />}
                  value={row.leads.toLocaleString()}
                  hint={`${paid ? 'Google Ads conversions' : 'on-page conversions'} · ${rangeLabel}`}
                />
                <StatTile
                  label={paid ? METRIC_LABEL.paidConversionRate : METRIC_LABEL.conversionRate}
                  onSelect={() => setMetric('cvr')}
                  selected={metric === 'cvr'}
                  labelAccessory={<InfoTip label="conversion rate" text={leadRateInfo} />}
                  value={cvrLabel(row.cvr)}
                  hint={`leads ÷ sessions · ${rangeLabel}`}
                />
                <StatTile
                  label="Clicks"
                  onSelect={() => setMetric('clicks')}
                  selected={metric === 'clicks'}
                  labelAccessory={<InfoTip label="clicks" text={METRIC_INFO.clicks} />}
                  value={row.clicks.toLocaleString()}
                  hint={`search clicks · ${rangeLabel}`}
                />
              </div>

              <Panel className="mt-4 p-5">
                <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h2 className="text-portal-card-title font-bold text-portal-ink">Over time</h2>
                    <p className="text-portal-micro text-portal-muted">
                      {INTERVAL_LABELS[effectiveInterval]} · {periodLabel(stats.data.range.from, 'day')} –{' '}
                      {periodLabel(stats.data.range.to, 'day')} (calendar days, UTC)
                    </p>
                  </div>
                  <div className="flex min-w-0 flex-wrap items-center gap-3">
                    <SegmentedControl<ChartMetric>
                      label="Metric"
                      value={metric}
                      onChange={setMetric}
                      options={(Object.keys(CHART_METRICS) as ChartMetric[]).map((key) => ({
                        key,
                        label: CHART_METRICS[key].label,
                      }))}
                    />
                    {/* CVR and position always draw as lines, so the switch only shows where it does something. */}
                    {metric !== 'cvr' && metric !== 'position' ? (
                      <SegmentedControl<TrendChartVariant>
                        label="Chart type"
                        value={variant}
                        onChange={setVariant}
                        options={CHART_VARIANTS.map((option) => ({ key: option.key, label: option.label }))}
                      />
                    ) : null}
                  </div>
                </div>
                {row.sessions === 0 && row.leads === 0 && row.impressions === 0 ? (
                  <EmptyState title="No traffic in this range" message="Try a wider range." />
                ) : (
                  <TrendChart
                    points={stats.data.buckets.map((bucket, index) => ({
                      key: bucket,
                      label: periodLabel(bucket, stats.data.range.interval),
                      value: typeValues(row, metric)[index] ?? 0,
                      details:
                        metric !== 'sessions' && (row.sessionsSeries[index] ?? 0) > 0
                          ? [`${(row.sessionsSeries[index] ?? 0).toLocaleString()} sessions`]
                          : [],
                    }))}
                    markers={markers}
                    // Rates read as lines; bars would make a worse (higher) position look bigger.
                    variant={metric === 'cvr' || metric === 'position' ? 'line' : variant}
                    title={`${CHART_METRICS[metric].label} · ${templateLabel(type)}`}
                    unit={CHART_METRICS[metric].unit}
                    formatValue={metric === 'cvr' ? (value) => `${value}%` : metric === 'position' ? (value) => value.toFixed(1) : compactCount}
                  />
                )}
              </Panel>
            </>
          )}

          <div className="mt-8 mb-3">
            <h2 className="text-portal-section font-bold text-portal-ink">Pages of this type</h2>
            <p className="text-portal-micro text-portal-muted">
              {row ? `${row.pages.toLocaleString()} ${row.pages === 1 ? 'page' : 'pages'} with traffic in the range, each against the ${rangeLabel} before. ` : ''}
              Click a page for its full history.
            </p>
          </div>
          <div className="mb-3 flex flex-wrap items-center gap-3">
            <SearchInput
              label="Search pages"
              placeholder="Path or title"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
              className="w-full max-w-sm"
            />
          </div>
          {pages.isError ? (
            <ErrorNotice title="Pages unavailable" message={errorMessage(pages.error)} />
          ) : pages.isPending ? (
            <LoadingState label="Loading pages…" />
          ) : pages.data.items.length === 0 && debounced !== '' ? (
            <Panel>
              <EmptyState title="No pages match" message="Try a different search." />
            </Panel>
          ) : pages.data.items.length === 0 ? (
            <Panel>
              <EmptyState title="No pages in this range" message="Pages of this type appear here after the first sync, once they have traffic." />
            </Panel>
          ) : (
            <>
              <DataTable
                headers={[
                  { label: 'Page', sortKey: PAGE_SORT.path },
                  { label: 'Sessions', sortKey: PAGE_SORT.sessions },
                  { label: 'Δ sessions', sortKey: PAGE_SORT.sessionsChange, info: <InfoTip label="change" text={METRIC_INFO.change} /> },
                  { label: 'Impressions', sortKey: PAGE_SORT.impressions },
                  { label: 'Δ impressions', sortKey: PAGE_SORT.impressionsChange },
                  { label: 'Position', sortKey: PAGE_SORT.position, info: <InfoTip label="average position" text={METRIC_INFO.position} /> },
                  { label: 'Δ position', sortKey: PAGE_SORT.positionChange, info: <InfoTip label="position change" text={METRIC_INFO.positionChange} /> },
                  { label: 'Leads', sortKey: PAGE_SORT.leads, info: <InfoTip label="leads" text={METRIC_INFO.leads} /> },
                  { label: METRIC_LABEL.conversionRate, sortKey: PAGE_SORT.leadRate, info: <InfoTip label="conversion rate" text={leadRateInfo} /> },
                  { label: METRIC_LABEL.firstSeen, sortKey: PAGE_SORT.firstSeen, info: <InfoTip label="first seen" text={METRIC_INFO.firstSeen} /> },
                  { label: METRIC_LABEL.lastSeen, sortKey: PAGE_SORT.lastSeen, info: <InfoTip label="last seen" text={METRIC_INFO.lastSeen} /> },
                ]}
                rightAlignedHeaders={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10]}
                sort={sort}
                onSort={onSort}
                minWidth={1200}
                cards={pages.data.items.map((pageRow) => ({
                  key: pageRow.path,
                  title: pageRow.path,
                  ...(pageRow.title ? { subtitle: pageRow.title } : {}),
                  fields: [
                    { label: 'Sessions', value: <>{pageRow.sessions.toLocaleString()} <PercentChange value={pageRow.sessionsChangePct} /></> },
                    { label: 'Impressions', value: <>{compactCount(pageRow.impressions)} <PercentChange value={pageRow.impressionsChangePct} /></> },
                    {
                      label: 'Position',
                      value: <>{positionLabel(pageRow.avgPosition)} <PositionChange value={pageRow.positionChange} previous={pageRow.previous?.avgPosition ?? null} /></>,
                    },
                    { label: METRIC_LABEL.leadsAndConversionRate, value: `${pageRow.leads.toLocaleString()} · ${leadRateLabel(pageRow.leadRate)}` },
                    { label: 'Clicks', value: pageRow.clicks.toLocaleString() },
                    { label: METRIC_LABEL.firstSeen, value: firstSeenLabel(pageRow.firstSeen, pageRow.firstSeenAtHistoryStart) },
                    { label: METRIC_LABEL.lastSeen, value: lastSeenLabel(pageRow.lastSeen) },
                  ],
                  onOpen: () => openPage(pageRow.path),
                }))}
              >
                {pages.data.items.map((pageRow) => (
                  <tr
                    key={pageRow.path}
                    onClick={() => openPage(pageRow.path)}
                    className="cursor-pointer border-t border-portal-line align-middle transition-colors hover:bg-portal-bg"
                  >
                    <td className="w-[260px] p-3 text-portal-small">
                      <span className="block font-bold [overflow-wrap:anywhere] text-portal-ink">{pageRow.path}</span>
                      {pageRow.title ? <span className="block text-portal-micro text-portal-muted">{pageRow.title}</span> : null}
                    </td>
                    <td className="p-3 text-right text-portal-small tabular-nums">{pageRow.sessions.toLocaleString()}</td>
                    <td className="p-3 text-right text-portal-small">
                      <PercentChange value={pageRow.sessionsChangePct} />
                    </td>
                    <td className="p-3 text-right text-portal-small tabular-nums text-portal-muted">{pageRow.impressions.toLocaleString()}</td>
                    <td className="p-3 text-right text-portal-small">
                      <PercentChange value={pageRow.impressionsChangePct} />
                    </td>
                    <td className="p-3 text-right text-portal-small tabular-nums">{positionLabel(pageRow.avgPosition)}</td>
                    <td className="p-3 text-right text-portal-small">
                      <PositionChange value={pageRow.positionChange} previous={pageRow.previous?.avgPosition ?? null} />
                    </td>
                    <td className="p-3 text-right text-portal-small tabular-nums font-bold text-portal-ink">{pageRow.leads.toLocaleString()}</td>
                    <td className="p-3 text-right text-portal-small tabular-nums text-portal-muted">{leadRateLabel(pageRow.leadRate)}</td>
                    <td className="p-3 text-right text-portal-small whitespace-nowrap text-portal-muted">
                      {firstSeenLabel(pageRow.firstSeen, pageRow.firstSeenAtHistoryStart)}
                    </td>
                    <td className="p-3 text-right text-portal-small whitespace-nowrap text-portal-muted">
                      {lastSeenLabel(pageRow.lastSeen)}
                    </td>
                  </tr>
                ))}
              </DataTable>
              <Pagination
                page={pages.data.pagination.page}
                totalPages={pages.data.pagination.totalPages}
                onChange={setPage}
                summary={`${(pages.data.pagination.page - 1) * PAGE_SIZE + 1}–${
                  (pages.data.pagination.page - 1) * PAGE_SIZE + pages.data.items.length
                } of ${pages.data.pagination.total.toLocaleString()} pages`}
                className="mt-3"
              />
            </>
          )}
        </>
      )}
    </section>
  );
}
